import { ReactNode } from 'react'

import { useLightMode } from '@/hooks/useLightMode'

import { DarkProvider, TopBar, TopBarItem } from '@hivewatch/dark'

import { DarkActionButtons } from '@/components/layouts/ActionButtons'

interface DarkAppHeaderIProps {
  /** Single page title. Ignored when `items` is provided. */
  title?: string
  /** Optional count rendered as a badge next to a single `title`. */
  count?: number
  /**
   * When true, renders a skeleton in place of the count badge — for totals
   * that resolve asynchronously. Restores the legacy header's loading
   * affordance. Only applies to the single-`title` form.
   */
  countLoading?: boolean
  /**
   * When true, renders a skeleton in place of the title — for titles that
   * resolve asynchronously (e.g. an org name). Restores the legacy header's
   * loading skeleton. Only applies to the single-`title` form.
   */
  titleLoading?: boolean
  /**
   * Full breadcrumb segments (chevron-separated), for headers with more than
   * one level, e.g. Settings › Account Management. Takes precedence over
   * `title`/`count`.
   */
  items?: TopBarItem[]
  /**
   * Right-aligned action slot. Defaults to the standard {@link DarkActionButtons}
   * (Emergency Call + Create). Pass a custom node for variant headers, or
   * `null` for a header with no actions.
   */
  actions?: ReactNode
  /**
   * Rendered as `data-testid` on the header container. Pass the same value the
   * legacy `Header` used so selectors stay stable across the feature flag.
   */
  dataTestId?: string
}

/**
 * Rebrand re-skin of the application header ({@link Header}): the Design
 * Library top navigation bar with a page title (or breadcrumb), an optional
 * count badge, and a right-aligned actions slot. Gated by
 * `FeatureFlag.appHeaderRebrand`. Wraps in its own `DarkProvider` so callers
 * can drop it straight into a page. See ENG-30681.
 */
export const DarkAppHeader = ({
  title,
  count,
  countLoading,
  titleLoading,
  items,
  actions = <DarkActionButtons />,
  dataTestId,
}: DarkAppHeaderIProps) => {
  const colorScheme = useLightMode()

  return (
    <DarkProvider>
      <TopBar
        actions={actions}
        colorScheme={colorScheme}
        dataTestId={dataTestId}
        items={
          items ?? [
            {
              label: title ?? '',
              count,
              loading: countLoading,
              labelLoading: titleLoading,
            },
          ]
        }
      />
    </DarkProvider>
  )
}
